import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../App";

const FREE_LESSONS = [
  {
    id: 1,
    title: "Why 95% of Traders Lose",
    duration: "5 min read",
    content: `The market isn't random—it's rigged against retail traders. Here's why:

**The Liquidity Game**
Every trade needs a counterparty. When you buy, someone sells to you. Institutions with billions in capital need YOUR stop losses to fill their massive orders.

**The Retail Trap**
- You place a stop loss at an obvious level
- Thousands of other traders do the same
- Institutions see this liquidity pool
- They push price to trigger stops
- They fill their orders at your expense
- Price reverses in the original direction

**The Solution**
Stop thinking like retail. Start thinking like the institutions hunting them. That's what you'll learn in the full Institutional Edge program.`,
    icon: "alert"
  },
  {
    id: 2,
    title: "The 1% Risk Rule",
    duration: "3 min read",
    content: `The single most important rule in trading:

**Never risk more than 1% of your account on any single trade.**

Why? Mathematics.

**At 1% risk per trade:**
- 10 consecutive losses = 10% drawdown
- You can recover easily
- You stay in the game

**At 5% risk per trade:**
- 10 consecutive losses = 50% drawdown
- You need 100% gain just to break even
- Most traders blow up here

**The Formula:**
Position Size = (Account × 0.01) / (Entry - Stop Loss)

Example: $10,000 account, buying at $100 with stop at $95
Position Size = ($10,000 × 0.01) / ($100 - $95) = $100 / $5 = 20 shares

This is non-negotiable. Master this before anything else.`,
    icon: "shield"
  },
  {
    id: 3,
    title: "What is an Order Block?",
    duration: "4 min read",
    content: `An Order Block is where institutions placed their orders. It's the footprint of smart money.

**How to Identify:**
- Look for the last DOWN candle before a strong UP move (Bullish OB)
- Look for the last UP candle before a strong DOWN move (Bearish OB)

**Why They Work:**
Institutions can't fill billion-dollar orders at once. They accumulate positions over time. When price returns to these zones, there are often unfilled orders waiting.

**The Edge:**
Instead of chasing breakouts like retail traders, you wait for price to return to order blocks. You enter where institutions entered—with them, not against them.

**Unlock the full methodology** including FU candles, liquidity grabs, and the Sniper Checklist in the premium program.`,
    icon: "target"
  }
];

export default function FreeTier() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const getIcon = (iconName) => {
    const icons = {
      alert: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      shield: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
        </svg>
      ),
      target: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>
      )
    };
    return icons[iconName] || icons.alert;
  };

  const formatContent = (text) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('**') && line.endsWith('**')) {
        return <p key={i} className="font-bold text-white my-3">{line.slice(2, -2)}</p>;
      }
      if (line.startsWith('- ')) {
        return (
          <div key={i} className="flex items-start gap-2 my-1 ml-4">
            <div className="w-1.5 h-1.5 bg-blue-500 mt-2 flex-shrink-0" />
            <span className="text-slate-400">{line.slice(2)}</span>
          </div>
        );
      }
      if (line.trim() === '') return <div key={i} className="h-2" />;
      return <p key={i} className="text-slate-400 my-1">{line}</p>;
    });
  };

  return (
    <div className="p-6 space-y-8" data-testid="free-tier-page">
      {/* Welcome Banner */}
      <div className="bg-gradient-to-r from-blue-500/10 to-transparent border border-blue-500/30 p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-mono text-blue-500 tracking-widest mb-2">WELCOME TO INSTITUTIONAL EDGE</p>
            <h1 className="text-2xl font-mono font-bold text-white mb-2">
              Hey {user?.name?.split(' ')[0]}, Ready to Beat the Rigged Game?
            </h1>
            <p className="text-slate-400 max-w-xl">
              You're on the <span className="text-yellow-500 font-mono">FREE TIER</span>. 
              Below are 3 foundational lessons to get you started. Upgrade to unlock the full 
              Institutional Edge platform with AI coaching, trade journal, and the complete methodology.
            </p>
          </div>
          <button
            onClick={() => navigate("/pricing")}
            className="btn-primary whitespace-nowrap"
            data-testid="upgrade-btn"
          >
            UPGRADE NOW
          </button>
        </div>
      </div>

      {/* What You're Missing */}
      <div className="card-institutional p-6">
        <h2 className="font-mono text-lg text-white mb-4">WHAT YOU'LL UNLOCK WITH PREMIUM</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { icon: "chart", label: "Live Dashboard", desc: "TradingView charts & analytics" },
            { icon: "book", label: "Trade Journal", desc: "Track every trade with metrics" },
            { icon: "check", label: "Sniper Checklist", desc: "16-point entry confirmation" },
            { icon: "coach", label: "AI Coach Bashirah", desc: "Personal trading mentor" },
          ].map((feature, i) => (
            <div key={i} className="bg-[#050505] border border-[#262626] p-4 opacity-60">
              <div className="flex items-center gap-3 mb-2">
                <div className="w-8 h-8 border border-slate-600 flex items-center justify-center">
                  <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                <span className="font-mono text-sm text-slate-400">{feature.label}</span>
              </div>
              <p className="text-xs text-slate-600">{feature.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Free Lessons */}
      <div>
        <h2 className="font-mono text-lg text-white mb-4">FREE LESSONS: START HERE</h2>
        <div className="space-y-6">
          {FREE_LESSONS.map((lesson) => (
            <div key={lesson.id} className="card-institutional overflow-hidden">
              <div className="p-4 border-b border-[#262626] flex items-center gap-4">
                <div className="w-10 h-10 border border-blue-500/30 bg-blue-500/10 text-blue-500 flex items-center justify-center">
                  {getIcon(lesson.icon)}
                </div>
                <div className="flex-1">
                  <h3 className="font-mono text-white font-bold">{lesson.title}</h3>
                  <p className="text-xs text-slate-500">{lesson.duration}</p>
                </div>
                <span className="px-2 py-1 text-xs font-mono bg-emerald-500/10 border border-emerald-500/30 text-emerald-500">
                  FREE
                </span>
              </div>
              <div className="p-6 text-sm">
                {formatContent(lesson.content)}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="card-institutional p-8 text-center border-blue-500/30">
        <h2 className="text-2xl font-mono font-bold text-white mb-4">
          Ready to Trade Like an Institution?
        </h2>
        <p className="text-slate-400 max-w-lg mx-auto mb-6">
          Stop losing to the rigged game. Get access to Coach Bashirah, the Sniper Checklist, 
          and the complete institutional methodology used by professional traders.
        </p>
        <button
          onClick={() => navigate("/pricing")}
          className="btn-primary px-8 py-4 text-lg animate-pulse-glow"
          data-testid="cta-upgrade-btn"
        >
          UNLOCK FULL ACCESS
        </button>
        <p className="text-xs text-slate-600 mt-4 font-mono">
          Starting at $49/month • Cancel anytime
        </p>
      </div>
    </div>
  );
}
